import { ISelectOption } from '@salecycle/ui-component-library';
import { Permission } from 'entities/user';
import { TFunction } from 'i18next';
import { Brand, Group, Site } from '../../client-management/entities';

export interface IGroups {
  allIds: string[];
  byId: { [key: string]: Group };
}

export interface IBrands {
  allIds: string[];
  byId: { [key: string]: Brand };
}

export interface ISites {
  allIds: string[];
  byId: { [key: string]: Site };
}

export interface IClients {
  groups: IGroups;
  brands: IBrands;
  sites: ISites;
}

export type Level = 'SaleCycle' | 'Group' | 'Brand' | 'Site' | 'MultiSite';

export type UserStatus =
  | 'UNCONFIRMED'
  | 'CONFIRMED'
  | 'ARCHIVED'
  | 'COMPROMISED'
  | 'UNKNOWN'
  | 'RESET_REQUIRED'
  | 'FORCE_CHANGE_PASSWORD';

export type UserType = 'INTERNAL' | 'EXTERNAL';

export interface Role {
  id: string;
  name: string;
  permissions: string[];
}

export interface INode {
  level: Level;
  id?: string;
}

export interface IPolicy {
  node: INode;
  permissions: Permission[];
  propagate: boolean;
}

export interface IPermission {
  label: string;
  value: Permission;
  fixed?: boolean;
}

export const getPermissions = (t: TFunction): IPermission[] => [
  { label: t('permissions.viewAll'), value: 'ViewAll' },
  { label: t('permissions.manageCampaigns'), value: 'ManageCampaigns' },
  { label: t('permissions.manageClients'), value: 'ManageClients' },
  { label: t('permissions.manageTrends'), value: 'ManageTrends' },
  { label: t('permissions.manageUsers'), value: 'ManageUsers' },
  { label: t('permissions.manageVoucherCodes'), value: 'ManageVoucherCodes' },
];

interface ICommonUserData {
  email: string;
  enabled: boolean;
  name: string;
  picture?: string;
  userCreateDate?: number;
  sub?: string;
  lastseen?: string;
  userStatus?: UserStatus;
  deleted?: boolean;
}

export interface IUserData extends ICommonUserData {
  policies?: IPolicy[];
  root?: INode;
  accessLevel: Level;
  role: string;
  groupIds: string[];
  brandIds: string[];
  siteIds: string[];
}

export interface ISftpUserData {
  sites?: string;
  brands?: string;
  groups?: string;
  username: string;
  email: string;
  enabled: boolean;
  userType: string;
}

export interface ISftpUserStatusUpdateData {
  username: string;
  enabled: boolean;
}

export interface ISftpUserResetPasswordData {
  username: string;
  email: string;
}

export interface ISftpUserUpdatePermissionsData {
  username: string;
  sites?: string;
  brands?: string;
  groups?: string;
}

export interface ISftpUserTableData extends ISftpUserData {
  createdDate?: number;
}

export interface IUserTableData extends ICommonUserData {
  accessLevel: Level;
  client: string;
  clientId: string;
}

export interface IMessage {
  message: string;
}

export interface IFilters {
  name: string;
  email: string;
  client: ISelectOption<string>;
  salecycleStaff: boolean;
  enabledUsers: boolean;
}

export interface ISftpFilters {
  username: string;
  email: string;
  userType: string;
  site: ISelectOption<string>;
  brand: ISelectOption<string>;
  group: ISelectOption<string>;
  enabledSftpUsers: boolean;
}

export interface IToggledUser {
  id: string;
  enabled: boolean;
}

/** Using the regex recommended by W3: http://www.w3.org/TR/html5/forms.html#valid-e-mail-address */
export const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
export const nameRegex = /^[^±§!@#€£$%^&*()_=+{}[\]:;"|\\<>?/`~,.0-9]*$/;
export const usernameRegex = /^[^±§ !@#€£$%^&*()_=+{}[\]:;"|\\<>?/`~,.0-9]*$/;
